import axios from 'axios';
import _ from 'underscore';
import TeamData from 'shared/data/Team';
import fileHelper from 'shared/data/helpers/file';

export default {
  /**
   * Login a user
   *
   */
  login(creds) {
    return _login(creds);
  },

  loginMFA(creds) {
    return _login(creds);
  },

  logout() {
    return axios.post('/logout');
  },

  /**
   * Load the user object from an active/valid token/session
   *
   */
  loadFromSession() {
    return axios.post('/api/token.getUser').then((resp) => resp.data);
  },

  /**
   * Check to see if user has two factor auth
   * @param email
   * @returns {*}
   */
  has2FactorAuthSecret(email) {
    return axios
      .post('/api/user.has2FactorAuthSecret', {
        params: { email: encodeURIComponent(email) },
      })
      .then((resp) => resp.data);
  },

  /**
   * Check if email is available for new user
   *
   */
  checkForExistingEmail(email) {
    return axios
      .post('/api/user.checkForExistingEmail', {
        params: { email: encodeURIComponent(email) },
      })
      .then((resp) => resp.data);
  },

  /**
   * Create/signup a new user
   *
   */
  signup(user) {
    return _signup(user);
  },

  /**
   * Create/signup a user and create a team
   */
  signupWithTeam(user, team, teamPhoto, userPhoto) {
    var ret = {};

    //Create the user
    return (
      _signup(user)
        //Set the user on the return object
        .then((createdUser) => {
          ret.user = createdUser;

          return user;
        })
        //Log the user in (create a session token)
        .then(_login.bind(this, _.pick(user, 'email', 'password')))
        .then(() => {
          if (!userPhoto) return;
          return _updatePhoto(ret.user._id, userPhoto);
        })
        //Create the team
        .then(() => {
          team.welcomeEmail = true;
          team.createdBy = {
            firstName: ret.user.firstName,
            lastName: ret.user.lastName,
          };
          return TeamData.create(team);
        })
        //Set the team object on the return value, and return
        .then(function (team) {
          ret.team = team;
          if (!teamPhoto) return ret;
          return TeamData.updatePhoto(team._id, teamPhoto);
        })
        .then(() => {
          return ret;
        })
    );
  },

  /**
   * Update a user preference..
   */
  updatePreference(userId, key, value) {
    return axios
      .post('/api/user.updatePreferences', {
        params: {
          userId,
          key,
        },
        payload: { value },
      })
      .then((res) => res.data);
  },

  /**
   * Update user onboarding
   */
  updateOnboarding(userId, key, value) {
    return axios
      .post('/api/user.updateOnboarding', {
        params: {
          userId,
          key,
        },
        payload: { value },
      })
      .then((res) => res.data);
  },

  /**
   * Update a user field..
   */
  updateField(userId, field, value) {
    let payload = {};
    payload[field] = value;

    return axios
      .post('/api/user.update', {
        params: {
          userId,
        },
        payload: payload,
      })
      .then((res) => res.data);
  },

  /**
   * Update a user
   */
  update(userId, changes) {
    return axios
      .post('/api/user.update', {
        params: {
          userId,
        },
        payload: changes,
      })
      .then((res) => res.data);
  },

  /**
   * Update a pass
   */
  updatePassword(userId, currentPassword, newPassword) {
    return axios
      .post('/api/user.updatePassword', {
        params: { userId },
        payload: { currentPassword, newPassword },
      })
      .then((res) => res.data);
  },

  /**
   * Update password from recovery token
   */
  updatePasswordFromRecoveryToken(email, token, password) {
    return axios
      .post('/api/user.updatePasswordFromRecoveryToken', {
        params: { email: encodeURIComponent(email) },
        payload: {
          passwordRecoveryToken: token,
          newPassword: password,
        },
      })
      .then((res) => res.data);
  },

  updatePhoto(userId, file) {
    return _updatePhoto(userId, file);
  },

  clearTrackableCue(userId, eventId, showId) {
    return axios
      .post('/api/user.clearTrackableCue', {
        params: { userId },
        payload: { eventId, showId },
      })
      .then((res) => res.data);
  },

  updateTrackableCue(userId, eventId, showId, itemSetId, itemSetRowId) {
    return axios
      .post('/api/user.updateTrackableCue', {
        params: { userId },
        payload: {
          eventId,
          showId,
          itemSetId,
          itemSetRowId,
          trackedOn: new Date().getTime(),
        },
      })
      .then((res) => res.data);
  },

  updateTrackableCueClock(
    userId,
    eventId,
    showId,
    startTime,
    ticking,
    durationInSeconds,
    originalDurationInSeconds,
  ) {
    return axios
      .post('/api/user.updateTrackableCueClock', {
        params: { userId },
        payload: {
          eventId,
          showId,
          startTime,
          ticking,
          durationInSeconds,
          originalDurationInSeconds,
        },
      })
      .then((res) => res.data);
  },

  updateTrackableCueAndCueClock(
    userId,
    eventId,
    showId,
    startTime,
    ticking,
    durationInSeconds,
    itemSetId,
    itemSetRowId,
  ) {
    return axios
      .post('/api/user.updateTrackableCueAndCueClock', {
        params: { userId },
        payload: {
          eventId,
          showId,
          itemSetId,
          itemSetRowId,
          trackedOn: startTime,
          cueClock: {
            showId,
            startTime,
            ticking,
            durationInSeconds,
          },
        },
      })
      .then((res) => res.data);
  },

  updateTrackingUser(userId, eventId, trackUserId) {
    return axios
      .post('/api/user.updateTrackingUser', {
        params: { userId, eventId },
        payload: { trackUserId },
      })
      .then((res) => res.data);
  },

  clearTracking(userId, eventId) {
    return axios
      .post('/api/user.clearTracking', {
        params: { userId, eventId },
      })
      .then((res) => res.data);
  },

  /**
   * Get user by ID
   *
   */
  get(userId) {
    return axios
      .post('/api/user.get', {
        params: {
          userId: userId,
        },
      })
      .then((res) => res.data);
  },

  /**
   * Get user by email
   *
   */
  resendInviteToken(email) {
    return axios
      .post('/api/user.resendInviteToken', {
        params: { email },
      })
      .then((res) => res.data);
  },

  /**
   * Get user email by token
   *
   */
  getByInviteToken(inviteToken) {
    return axios
      .post('/api/user.getByInviteToken', {
        params: { inviteToken },
      })
      .then((res) => res.data);
  },
};

function _signup(user) {
  return axios
    .post('/api/user.create', {
      payload: user,
    })
    .then((resp) => resp.data);
}

function _updatePhoto(userId, file) {
  return fileHelper.upload(
    '/api/upload/user.updatePhoto',
    { userId: userId },
    file,
  );
}

function _login(creds) {
  return axios.post('/login', creds).then((res) => res.data);
}
